import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { DistributionConstraints, SearchAccountsResponsePayload } from '@Types'
import { useFormikContext } from 'formik'
import { withFormikDevtools } from 'formik-devtools-extension'
import { Text } from '@chakra-ui/react'
import { TransferDirection } from '@Types'
import { numberInputFormatter, useFormatNumber, getAllAccountNames } from '../../utils'
import { TransferAmount } from '../TransferAmount'
import { CheckTransferValues } from './Types'
import CheckRecipientForm from '../../components/CheckRecipientForm'
import { CountryItem } from '../../reducers/types'
import CheckMemosForm from '../CheckMemosForm'
import IraDistributionForm from '../../components/IraDistributionForm'
import { DistributionReasonInfo, RetirementConstraintsResponse } from '@Types'

interface Props {
  account: SearchAccountsResponsePayload
  amountAvailable: number
  transferDirection?: TransferDirection
  constraintData: RetirementConstraintsResponse
  getCountryList: () => void
  countryList: CountryItem[]
  countryListLoading: boolean
  countryListFailedMessage?: string
  countryAutoSelectDataSet: { value: string }[]
  hasOvernightToApexPerm: boolean
  hasTotalDistributionError: boolean
  setHasTotalDistributionError: (value: boolean) => void
  handleSelectReason: (reason: DistributionReasonInfo | null) => void
  selectedReason?: DistributionReasonInfo | null
}

function CheckTransferDetails({
  account,
  amountAvailable,
  transferDirection,
  constraintData,
  getCountryList,
  countryList,
  countryListLoading,
  countryListFailedMessage,
  countryAutoSelectDataSet,
  hasOvernightToApexPerm,
  hasTotalDistributionError,
  setHasTotalDistributionError,
  handleSelectReason,
  selectedReason
}: Props): ReactElement {
  const [distributionConstraints, setDistributionConstraints] = useState<DistributionConstraints | null>(null)

  const formik = useFormikContext<CheckTransferValues>()
  const {
    errors,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    setValues,
    setTouched,
    touched,
    values
  } = formik
  withFormikDevtools(formik)

  const clearTransferAmount = () => {
    setFieldValue('transferAmountDisplay', '')
    setFieldValue('transferAmountRaw', 0)
    setFieldTouched('transferAmountRaw', false)
  }

  const clearTransferAmountMemo = useCallback(clearTransferAmount, [])
  useEffect(() => {
    if (values.totalBalanceCheckbox) {
      clearTransferAmountMemo()
    }
  }, [values.totalBalanceCheckbox, clearTransferAmountMemo])

  useEffect(() => {
    if (
      constraintData?.distributionConstraints?.totalDistributionAllowed === false &&
      formik.values.totalBalanceCheckbox
    ) {
      setHasTotalDistributionError(true)
    } else {
      setHasTotalDistributionError(false)
    }
  }, [formik.values.totalBalanceCheckbox, constraintData, setHasTotalDistributionError])

  useEffect(() => {
    if (constraintData) {
      //@ts-ignore
      setDistributionConstraints(constraintData.distributionConstraints)
    }
  }, [constraintData])

  const numberFormatter = useFormatNumber()
  const allAccountNames = getAllAccountNames(account.accountTitle, account.accountNames)

  return (
    <>
      <TransferAmount
        value={values.transferAmountDisplay}
        isDisabled={values.totalBalanceCheckbox}
        errorMessage={errors.transferAmountDisplay || errors.transferAmountRaw}
        handleBlur={handleBlur}
        handleAmountChange={(e) => {
          setValues({
            ...formik.values,
            transferAmountDisplay: numberInputFormatter(e.target.value, numberFormatter).display,
            transferAmountRaw: numberInputFormatter(e.target.value, numberFormatter).raw as number
          })
        }}
        isInvalid={
          (!!errors.transferAmountRaw && touched.transferAmountRaw) ||
          (!!errors.transferAmountRaw && values.totalBalanceCheckbox)
        }
        totalBalanceChecked={values.totalBalanceCheckbox}
        handleCheckboxChange={handleChange}
      />
      {hasTotalDistributionError && (
        <Text color={'red.500'}>This type of IRA account does not support a total amount transfer.</Text>
      )}
      <CheckRecipientForm
        formikValues={values}
        formikTouched={touched}
        formikBlur={handleBlur}
        setFieldValue={setFieldValue}
        errors={errors}
        setTouched={setTouched}
        accountNames={allAccountNames}
        accountNumber={account.accountNumber}
        countryList={countryAutoSelectDataSet}
        countryListLoading={countryListLoading}
        countryListFailedMessage={countryListFailedMessage}
        getCountryList={getCountryList}
        hasOvernightToApexPerm={hasOvernightToApexPerm}
      />
      <CheckMemosForm
        formikValues={values}
        formikTouched={touched}
        formikBlur={handleBlur}
        setFieldValue={setFieldValue}
        errors={errors}
      />
      {constraintData?.retirementAccount ? (
        <IraDistributionForm
          formikSetValues={setValues}
          formikValues={values}
          formikBlur={handleBlur}
          formikTouched={touched}
          setFieldValue={setFieldValue}
          setTouched={setTouched}
          errors={errors}
          selectedReason={selectedReason}
          handleSelectReason={handleSelectReason}
          distributionReasonOptions={distributionConstraints?.validDistributionReasons}
          setFieldTouched={setFieldTouched}
        />
      ) : null}
    </>
  )
}

export default CheckTransferDetails
